import { TextField } from '@mui/material';
import { updateTierThunk, getTierByIdThunk, getTiersThunk } from '@redux/features/tier.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    tierId: number;
};

const UpdateTierModal: FC<IProps> = ({ show, handleClose, tierId }) => {
    const { loading, tier } = useAppSelector((state: RootState) => state.tierState);
    // ----------------------------------------------------------------
    const dispatch = useAppDispatch();
    // --------------------------------------------------
    const validationSchema = Yup.object({
        percentageTier: Yup.number()
            .required('Percentage Tier Is Required')
            .min(0, 'Percentage must be 0 or greater')
            .max(100, 'Percentage must be 100 or less'),
    });
    const handleSubmit = (values: any) => {
        dispatch(updateTierThunk({ tierId: tierId, percentageTier: Number(values.percentageTier) })).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getTiersThunk());
                handleClose();
                formik.resetForm();
            }
        });
    };
    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            percentageTier: tier?.percentageTier || 0,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });
    // --------------------------------------------------
    useEffect(() => {
        if (show && tierId) {
            dispatch(getTierByIdThunk(tierId));
        }
    }, [show, tierId, dispatch]);

    useEffect(() => {
        if (tier && show) {
            formik.setValues({
                percentageTier: tier.percentageTier || 0,
            });
        }
    }, [tier, show]);

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Tier</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <TextField
                        disabled={loading}
                        fullWidth
                        size="small"
                        type="number"
                        id="percentageTier"
                        name="percentageTier"
                        label="Percentage Tier"
                        value={formik.values.percentageTier}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.percentageTier && Boolean(formik.errors.percentageTier)}
                        helperText={formik.touched.percentageTier ? String(formik.errors.percentageTier || '') : ''}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={loading}
                />
                <ButtonBase
                    innerText="Update Tier"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={loading || formik.values.percentageTier === undefined || formik.values.percentageTier === null}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateTierModal;

